// Reading an ATLAS claim (the "Recipient created tax invoice" VLA issues for each claim).
//
// Every claim has the same two parts. "Claim Details" names the grant ("File/Ext
// 25A816635/0" is grant 25A816635, extension 0), the client and the claim. "Work Performed"
// is a table with one row per fee claimed, in VLA's own fee names:
//
//   Work type              Units  Services Date  GST     GST Incl
//   PSYCHOLOGICAL REPORT   -      -              $67.82  $746.00
//
// Those fee names are the ones the claim tracker's allowance rows use, so each row counts
// against the fee it used up rather than a guess from the claim's wording.
//
// Like approvalLetterGrants, this takes the claim as lines in reading order.

function clean(value = "") {
  return String(value).replace(/\u0000/g, "").replace(/\s+/g, " ").trim();
}

function toIso(value = "") {
  const match = String(value).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return match ? `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}` : "";
}

function money(value = "") {
  const amount = Number(String(value).replace(/[$,]/g, ""));
  return Number.isFinite(amount) ? amount : null;
}

const DATE = String.raw`\d{1,2}\/\d{1,2}\/\d{4}`;
const WORK_ROW = new RegExp(String.raw`^(.+?)\s+(\d+(?:\.\d+)?|-)\s+((?:${DATE})(?:\s*(?:-|–|to)\s*(?:${DATE}|current))?|-)\s+\$\s?([\d,]+\.\d{2})\s+\$\s?([\d,]+\.\d{2})$`, "i");

export function isAtlasClaim(lines = []) {
  const text = lines.map(clean).join("\n");
  return /Recipient created tax invoice/i.test(text) && /Claim Details/i.test(text) && /Work Performed/i.test(text);
}

export function readAtlasClaim(rawLines = []) {
  const lines = rawLines.map(clean).filter(Boolean);
  const text = lines.join("\n");
  const field = (label) => clean(text.match(new RegExp(`${label}\\s+([^\\n]+?)(?:\\s{2,}|\\s+(?:Final Claim|$)|\\n)`, "im"))?.[1] || "");

  const fileExt = text.match(/File\/Ext\s+(\d{2}A\d{6})\s*\/\s*(\d+)/i);
  const result = {
    claimId: text.match(/Claim ID\s+(\d+)/i)?.[1] || "",
    proformaInvoiceId: text.match(/Proforma Invoice ID\s+(\d+)/i)?.[1] || "",
    aidNumber: fileExt?.[1]?.toUpperCase() || "",
    extensionNumber: fileExt ? String(Number(fileExt[2])) : "",
    clientName: field("Client Name"),
    yourFileReference: field("Your File Reference"),
    yourClaimReference: field("Your Claim Reference"),
    practitioner: field("Practitioner"),
    submittedDate: toIso(text.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s+\d{1,2}:\d{2}:\d{2}\s*[AP]M/i)?.[1] || ""),
    finalClaimForFile: /Final Claim for this File\?\s*YES/i.test(text),
    items: [],
    total: null,
  };

  const start = lines.findIndex((line) => /^Work type\b/i.test(line));
  for (let i = start + 1; start >= 0 && i < lines.length; i += 1) {
    const line = lines[i];
    const total = line.match(/^Total\s+\$\s?([\d,]+\.\d{2})\s+\$\s?([\d,]+\.\d{2})$/i);
    if (total) {
      result.total = money(total[2]);
      break;
    }
    const row = line.match(WORK_ROW);
    if (!row) continue;
    const dates = row[3] === "-" ? [] : [...row[3].matchAll(new RegExp(DATE, "g"))].map((match) => toIso(match[0]));
    result.items.push({
      workType: clean(row[1]).toUpperCase(),
      units: row[2] === "-" ? "" : row[2],
      serviceDate: dates[0] || "",
      serviceDateTo: dates[1] || (/current/i.test(row[3]) ? "current" : ""),
      gst: money(row[4]),
      amount: money(row[5]),
    });
  }

  return result;
}
